import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IndexDBService {
  private dbName = 'tibss_db';
  private dbVersion = 7; // Incrementing version to force upgrade
  private db: IDBDatabase | null = null;
  private initializationPromise: Promise<void> | null = null;
  private readonly STORE_NAME = 'resource_categories';

  // Observable to track database readiness
  private dbReady = new BehaviorSubject<boolean>(false);
  dbReady$ = this.dbReady.asObservable();

  constructor() {

    this.initDB();
  }

  private initDB(): Promise<void> {

    if (this.initializationPromise) {

      return this.initializationPromise;
    }

    this.initializationPromise = new Promise((resolve, reject) => {

      
      // Delete the existing database first
      const deleteRequest = indexedDB.deleteDatabase(this.dbName);
      
      deleteRequest.onsuccess = () => {

        // Now create a new database
        const request = indexedDB.open(this.dbName, this.dbVersion);

        request.onerror = (event) => {
          console.error('❌ Error opening IndexedDB:', event);
          this.dbReady.next(false);
          reject(event);
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
      
          
          // Ensure the store exists
          if (!this.db.objectStoreNames.contains(this.STORE_NAME)) {
       
            this.db.close();
            this.dbVersion++;
            this.initializationPromise = null;
            this.initDB();
            return;
          }
          
          this.dbReady.next(true);
          resolve();
        };

        request.onupgradeneeded = (event) => {

          const db = (event.target as IDBOpenDBRequest).result;

          // Delete existing store if it exists
          if (db.objectStoreNames.contains(this.STORE_NAME)) {
           
            db.deleteObjectStore(this.STORE_NAME);
          }

     
          // Create store with keyPath as 'id' since each resource has a unique id
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          
          // Add indexes for commonly used fields
          store.createIndex('Name', 'Name', { unique: false });
          store.createIndex('Description', 'Description', { unique: false });
          

        };
      };

      deleteRequest.onerror = () => {
        console.error('❌ Error deleting old database');
        // Continue with opening anyway
        const request = indexedDB.open(this.dbName, this.dbVersion);
        // ... same handlers as above
      };
    });

    return this.initializationPromise;
  }

  /**
   * Ensures the database is initialized before performing operations
   */
  private async ensureDBInitialized(): Promise<void> {
    if (!this.db) {

      await this.initDB();
    }
  }

  /**
   * Add or update resource categories
   */
  async addResourceCategory(data: any[]): Promise<void> {

    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        const error = new Error('Database not initialized');
        console.error('❌', error);
        reject(error);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);

        // Clear existing data first
        const clearRequest = store.clear();
        
        clearRequest.onsuccess = () => {
        
          
          // Add each resource
          let addedCount = 0;
          data.forEach((item) => {
            try {
              const request = store.add(item);
              request.onsuccess = () => {
                addedCount++;
       
              };
              request.onerror = (e) => {
                console.error('Error adding item:', e, item);
              };
            } catch (e) {
              console.error('Error processing item:', e, item);
            }
          });
        };

        transaction.oncomplete = () => {
  
          resolve();
        };

        transaction.onerror = (e) => {
          console.error('❌ Transaction error:', e);
          reject(transaction.error);
        };
      } catch (error) {
        console.error('❌ Error in addResourceCategory:', error);
        reject(error);
      }
    });
  }

  /**
   * Get all resource categories
   */
  async getAllResourceCategories(): Promise<any[]> {

    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        const error = new Error('Database not initialized');
        console.error('❌', error);
        reject(error);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          const data = request.result;
       
          resolve(data);
        };

        request.onerror = () => {
          console.error('❌ Error getting resource categories:', request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('❌ Error in getAllResourceCategories:', error);
        reject(error);
      }
    });
  }

  /**
   * Get a resource category by ID
   */
  async getResourceCategoryById(id: number): Promise<any> {

    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        const error = new Error('Database not initialized');
        console.error('❌', error);
        reject(error);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(Number(id));

        request.onsuccess = () => {
          if (request.result) {
         
            // Normalize data to ensure Name property
            if (!request.result.Name && request.result.IsActive) {
              request.result.Name = request.result.IsActive;
              delete request.result.IsActive;
            }
          } else {

          }
          resolve(request.result);
        };

        request.onerror = () => {
          console.error(`❌ Error getting resource category ${id}:`, request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('❌ Error in getResourceCategoryById:', error);
        reject(error);
      }
    });
  }

  /**
   * Get a resource category by Name
   */
  async getResourceCategoryByName(name: string): Promise<any> {

    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        const error = new Error('Database not initialized');
        console.error('❌', error);
        reject(error);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(name);

        request.onsuccess = () => {
          if (request.result) {

          } else {
  
          }
          resolve(request.result);
        };

        request.onerror = () => {
          console.error(`❌ Error getting resource category ${name}:`, request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('❌ Error in getResourceCategoryByName:', error);
        reject(error);
      }
    });
  }

  /**
   * Clear all resource categories
   */
  async clearResourceCategories(): Promise<void> {
   
    await this.ensureDBInitialized();

    return new Promise((resolve, reject) => {
      if (!this.db) {
        const error = new Error('Database not initialized');
        console.error('❌', error);
        reject(error);
        return;
      }

      try {
        const transaction = this.db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {

          resolve();
        };

        request.onerror = () => {
          console.error('❌ Error clearing resource categories:', request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('❌ Error in clearResourceCategories:', error);
        reject(error);
      }
    });
  }

  /**
   * Clear all data from IndexedDB
   */
  async clearAllData(): Promise<void> {

    
    if (this.db) {

      this.db.close();
      this.db = null;
    }

    return new Promise((resolve, reject) => {

      const request = indexedDB.deleteDatabase(this.dbName);

      request.onsuccess = () => {
 
        this.initializationPromise = null;
        this.initDB();
        resolve();
      };

      request.onerror = () => {
        console.error('❌ Error deleting database:', request.error);
        reject(request.error);
      };
    });
  }
} 